// FIRM (TENANT) HOUSE-POLICY REQUIRED DOCUMENTS (v1_702). A tenant's own AML
// policy can mandate documents to collect from clients — captured operator-side
// two ways, both under firms.compliance_config:
//   - documentChecklist: { common: [...], byType: { <ENTITY_TYPE>: [...] } } —
//     operator-curated, already keyed to the controlled entity-type vocabulary;
//   - housePolicy.fields.requiredDocsByEntityType — distilled by the AI from the
//     firm's uploaded AML policy, with FREE-FORM entity-type keys.
// These items are a fixed requirement of the tenant's policy: the proportionality
// pass must never cut, downgrade or merge one. This module is the single, pure,
// per-tenant source of truth both the deterministic Guard 0 and the pass prompt
// read from — always resolved against ONE firm's config and ONE case's entity
// type, never a global list.

import { CHECKLIST_KEY_TO_BOARD_TYPES, floorBoardTypeFor, questionnaireTypeForBoardType, QUESTIONNAIRE_TO_BOARD_TYPES, SPECIFIC_DOC_TYPES, adminSpecificDocs } from './policy-starter-docs.js';

// The controlled entity-type vocabulary (mirrors prepare-invite / the board schema).
export const ENTITY_TYPES = Object.freeze([
  'VASP', 'SPOT_BROKER', 'CFD_BROKER', 'PROP_FIRM', 'REGULATED_FUND', 'ASSET_MANAGER',
  'FAMILY_OFFICE', 'CORPORATE_TREASURY', 'GAMING', 'MSB', 'OTHER'
]);

// A firm-wide requirement (applies to every client regardless of entity type).
export const ALL_ENTITY_TYPES = 'ALL';

// Map a free-form entity-type label (as the distiller may have written it) to the
// controlled vocabulary. Returns a controlled type, ALL_ENTITY_TYPES for a
// firm-wide key, or the trimmed upper-cased original when nothing maps (so a key
// is never silently dropped — it is preserved, it just won't match a controlled
// board type). Deterministic; no fuzzy scoring.
export function normalizeEntityTypeKey(key) {
  const k = String(key || '').trim();
  if (!k) return '';
  const up = k.toUpperCase().replace(/[\s-]+/g, '_');
  if (ENTITY_TYPES.includes(up)) return up;
  const s = k.toLowerCase();
  if (/\b(all|any|every|common|general|firm[- ]?wide)\b/.test(s)) return ALL_ENTITY_TYPES;
  if (/fund|collective investment|ucits|\baif\b|sicav/.test(s)) return 'REGULATED_FUND';
  if (/asset manag|investment manag|portfolio manag|wealth manag|discretionary manag/.test(s)) return 'ASSET_MANAGER';
  if (/family office/.test(s)) return 'FAMILY_OFFICE';
  if (/treasury/.test(s)) return 'CORPORATE_TREASURY';
  if (/vasp|virtual asset|crypto|digital asset|exchange|custodian/.test(s)) return 'VASP';
  if (/gaming|gambling|casino|igaming|betting/.test(s)) return 'GAMING';
  if (/\bmsb\b|money service|payment institution|\bemi\b|e-?money|remittance|money transmi/.test(s)) return 'MSB';
  if (/spot (broker|fx|trading)/.test(s)) return 'SPOT_BROKER';
  if (/\bcfd\b|contract for difference/.test(s)) return 'CFD_BROKER';
  if (/prop(rietary)?[\s-]?(firm|trading|shop)|prop firm/.test(s)) return 'PROP_FIRM';
  return up; // preserved, uncontrolled
}

const cleanDocList = (v) => (Array.isArray(v) ? v : []).map(x => String(x || '').trim()).filter(Boolean);

// Normalise a distilled requiredDocsByEntityType object (free-form keys, arrays
// of doc labels) to controlled keys, merging entries that map to the same type.
// Persisted at operator-approval time so the read path is a plain lookup, not a
// per-call re-derivation. Pure and idempotent.
export function normalizeRequiredDocsByEntityType(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [key, val] of Object.entries(raw)) {
    const nk = normalizeEntityTypeKey(key);
    if (!nk) continue;
    const docs = cleanDocList(val);
    if (!docs.length) continue;
    out[nk] = out[nk] ? [...out[nk], ...docs] : docs;
  }
  // dedupe each bucket
  for (const k of Object.keys(out)) out[k] = [...new Set(out[k])];
  return out;
}

// THE per-tenant floor for one case. Returns the distinct list of document labels
// the firm's own policy mandates for a client of this entity type — the firm's
// common (all-client) requirements plus this entity type's requirements, drawn
// from BOTH the operator-curated documentChecklist and the distilled house
// policy. Always scoped to ONE firm's compliance_config; an absent/empty config
// yields [] (the pass then behaves exactly as it did before this floor existed).
// v1_832: opts.legalGroup, the questionnaire group the client's legal form
// places it in (partnership, trust or individual; src/shared/legal-form.js).
// With a signed questionnaire it picks the broad list ahead of the entity
// type's group; a board type with a list of its own keeps that list (v1_818).
export function firmMandatedDocs(complianceConfig, entityType, opts = {}) {
  const cc = complianceConfig && typeof complianceConfig === 'object' ? complianceConfig : {};
  const type = normalizeEntityTypeKey(entityType);
  const out = [];

  // 1. Operator-curated document checklist (already controlled-vocab keyed).
  const dc = cc.documentChecklist && typeof cc.documentChecklist === 'object' ? cc.documentChecklist : {};
  out.push(...cleanDocList(dc.common));
  if (dc.byType && typeof dc.byType === 'object') {
    if (type && Array.isArray(dc.byType[type])) out.push(...cleanDocList(dc.byType[type]));
    if (Array.isArray(dc.byType[ALL_ENTITY_TYPES])) out.push(...cleanDocList(dc.byType[ALL_ENTITY_TYPES]));
    // v1_814: the admin checklist offers BROKER and HOLDING_SPV, which no board
    // type ever carries, so documents listed under them protected nothing. They
    // now protect the board types they describe (approved mapping).
    for (const [ck, boardTypes] of Object.entries(CHECKLIST_KEY_TO_BOARD_TYPES)) {
      if (type && boardTypes.includes(type) && Array.isArray(dc.byType[ck])) out.push(...cleanDocList(dc.byType[ck]));
    }
  }

  // 2. Distilled house policy. Prefer the normalized structure persisted at
  //    approval; fall back to normalizing the raw at read (older firms).
  const hpFields = (cc.housePolicy && cc.housePolicy.fields) || {};
  const normalized = (hpFields.requiredDocsByEntityTypeNormalized && typeof hpFields.requiredDocsByEntityTypeNormalized === 'object')
    ? hpFields.requiredDocsByEntityTypeNormalized
    : normalizeRequiredDocsByEntityType(hpFields.requiredDocsByEntityType);
  // v1_818: A SPECIFIC LIST KEEPS PRECEDENCE. Once the firm has signed its
  // policy questionnaire, the questionnaire's broad list for a type applies
  // only to a board type with no list of its own. A type with its own list
  // (the admin checklist's byType, read above, or a list the questionnaire
  // gave that one type) keeps exactly that list; the broad list is never added
  // on top. A floor signed before v1_818 has no requiredDocsBroad: its stored
  // lists are all broad, and the same precedence applies to them.
  if (hpFields.requiredDocsSource === 'policy_questionnaire') {
    const bt = type && SPECIFIC_DOC_TYPES.includes(type) ? type : floorBoardTypeFor(entityType);
    const specificTypes = Array.isArray(hpFields.requiredDocsSpecificTypes) ? hpFields.requiredDocsSpecificTypes : [];
    const broad = hpFields.requiredDocsBroad && typeof hpFields.requiredDocsBroad === 'object' ? hpFields.requiredDocsBroad : null;
    if (bt && specificTypes.includes(bt) && Array.isArray(normalized[bt])) out.push(...normalized[bt]);
    else if (type && adminSpecificDocs(dc, type).length) { /* the admin checklist's own list, read above, stands alone */ }
    else if (broad) {
      const lg = opts && opts.legalGroup;
      const qt = (lg && Array.isArray(broad[lg])) ? lg : (bt && questionnaireTypeForBoardType(bt));
      if (qt && Array.isArray(broad[qt])) out.push(...cleanDocList(broad[qt]));
    } else if (type) {
      const qt = bt && questionnaireTypeForBoardType(bt);
      const key = Array.isArray(normalized[type]) ? type
        : bt && Array.isArray(normalized[bt]) ? bt
        : (qt ? QUESTIONNAIRE_TO_BOARD_TYPES[qt].find(k => Array.isArray(normalized[k])) : null);
      if (key) out.push(...normalized[key]);
    }
  } else if (type && Array.isArray(normalized[type])) out.push(...normalized[type]);
  // v1_814b: a board type outside the floor's own keys (the sector vocabulary,
  // or a model invented value such as OPERATING_COMPANY) reads the list of the
  // board key it resolves to (unsigned distilled policy).
  else if (type) {
    const bt = floorBoardTypeFor(entityType);
    if (bt && Array.isArray(normalized[bt])) out.push(...normalized[bt]);
  }
  if (Array.isArray(normalized[ALL_ENTITY_TYPES])) out.push(...normalized[ALL_ENTITY_TYPES]);

  return [...new Set(out.map(s => String(s || '').trim()).filter(Boolean))];
}

// v1_812: WHAT THE REVIEW BANNER MAY SAY ABOUT THE FLOOR. The stamp read only
// firmMandatedDocs(...).length, so a tenant whose distilled house policy is
// loaded but names no documents for THIS entity type (an empty
// requiredDocsByEntityType, or keys that do not normalise to the case's type)
// was told "No firm house policy floor is configured for this tenant" (Sage
// Capital Management, Fig Markets case, production 25/09/26). Pure read: the
// documents protected are firmMandatedDocs, unchanged; this only names the state.
//   configured  the floor holds at least one document for this case
//   no_match    a policy or checklist is loaded, but names nothing for this type
//   none        no house policy and no checklist is loaded for the tenant
// v1_814 adds one state, and it takes the place of no_match where it applies:
//   default_pending  a manual is loaded, the firm has not signed its policy
//                    questionnaire, and the floor is empty for this type, so the
//                    review ran on CompliantID defaults (general AML principles)
//                    pending the firm's confirmation. Never "none configured"
//                    while a manual is loaded.
export function questionnaireSigned(complianceConfig) {
  const f = complianceConfig && complianceConfig.housePolicy && complianceConfig.housePolicy.fields;
  return !!(f && f.requiredDocsSource === 'policy_questionnaire');
}
export function firmFloorState(complianceConfig, entityType, opts = {}) {
  const cc = complianceConfig && typeof complianceConfig === 'object' ? complianceConfig : {};
  const docs = firmMandatedDocs(cc, entityType, opts);
  const hp = cc.housePolicy && typeof cc.housePolicy === 'object' ? cc.housePolicy : null;
  const hpLoaded = !!(hp && ((typeof hp.text === 'string' && hp.text.trim()) || (typeof hp.summary === 'string' && hp.summary.trim()) || (hp.fields && Object.keys(hp.fields).length)));
  const dc = cc.documentChecklist && typeof cc.documentChecklist === 'object' ? cc.documentChecklist : null;
  const dcLoaded = !!(dc && ((Array.isArray(dc.common) && dc.common.length) || (dc.byType && Object.values(dc.byType).some(v => Array.isArray(v) && v.length))));
  const loaded = hpLoaded || dcLoaded;
  const manualLoaded = !!(hp && ((typeof hp.docPath === 'string' && hp.docPath) || (typeof hp.text === 'string' && hp.text.trim())));
  const state = docs.length ? 'configured'
    : (manualLoaded && !questionnaireSigned(cc)) ? 'default_pending'
    : (loaded ? 'no_match' : 'none');
  return { state, docs, loaded, entityTypeKey: normalizeEntityTypeKey(entityType) || '', legalGroup: (opts && opts.legalGroup) || null };
}
